import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, CheckCircle2, Clock, FileImage, Repeat, ExternalLink, Download } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import ImageLightbox from "@/components/ImageLightbox";
import { STATUS_LABEL, CORRIDORS, DEPOSIT_METHODS, RECEPTION_METHODS, formatAmount, formatDate } from "@/lib/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal/sheet rendering the full timeline + proofs of a user-owned transaction.
 * Props: code (string), onClose ()
 */
export default function TransactionDetailModal({ code, onClose }) {
  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lightbox, setLightbox] = useState(null); // { src, alt }

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const { data } = await api.get(`/transactions/track/${code}`);
        if (!cancelled) setTx(data);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e?.response?.data?.detail) || "Erreur de chargement");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  if (!code) return null;

  const cdef = tx ? CORRIDORS[tx.corridor || "tg_ga"] : null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="tx-detail-modal"
    >
      <div className="bg-[color:var(--sl-card-bg)] text-[color:var(--sl-text)] w-full sm:max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-2xl shadow-2xl border border-[color:var(--sl-border)]">
        <div className="sticky top-0 bg-[color:var(--sl-card-bg)] border-b border-[color:var(--sl-border)] px-5 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="font-mono text-base sm:text-lg font-bold text-[var(--sl-primary)] truncate" data-testid="tx-detail-code">{code}</span>
            {tx && <StatusBadge status={tx.status} cancellationReason={tx.cancellation_reason} />}
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full grid place-items-center hover:bg-[color:var(--sl-bg-alt)] transition"
            aria-label="Fermer"
            data-testid="tx-detail-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {loading && <div className="text-center text-sm text-gray-500 py-10">Chargement…</div>}
          {error && !loading && (
            <div className="text-center text-sm text-red-600 py-6">{error}</div>
          )}
          {tx && !loading && (
            <>
              {/* Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Card label="Corridor" value={`${cdef?.flagFrom || ""} ${cdef?.short || ""}`} />
                <Card label="Montant envoyé" value={formatAmount(tx.amount_xof, tx.source_currency || "XOF")} />
                <Card label="Reçu par le bénéficiaire" value={formatAmount(tx.amount_xaf, tx.target_currency || "XAF")} />
                <Card label="Frais" value={formatAmount(tx.fee, tx.source_currency || "XOF")} />
                <Card label="Taux" value={`× ${tx.exchange_rate}`} />
                <Card label="Créée le" value={formatDate(tx.created_at)} />
              </div>

              {/* Parties */}
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="rounded-xl border border-[color:var(--sl-border)] p-4">
                  <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Expéditeur</div>
                  <div className="text-sm font-medium">{tx.sender_name}</div>
                  <div className="text-xs text-gray-500">{tx.sender_phone}</div>
                  {tx.sender_email && <div className="text-xs text-gray-500">{tx.sender_email}</div>}
                  <div className="text-xs text-gray-400 mt-1">{DEPOSIT_METHODS[tx.deposit_method]?.label || tx.deposit_method}</div>
                  {tx.payment_reference && <div className="text-xs font-mono text-gray-500 mt-1">Réf: {tx.payment_reference}</div>}
                </div>
                <div className="rounded-xl border border-[color:var(--sl-border)] p-4">
                  <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Bénéficiaire</div>
                  <div className="text-sm font-medium">{tx.beneficiary_name}</div>
                  {tx.beneficiary_phone && <div className="text-xs text-gray-500">{tx.beneficiary_phone}</div>}
                  {tx.beneficiary_email && <div className="text-xs text-gray-500">{tx.beneficiary_email}</div>}
                  {tx.beneficiary_account && <div className="text-xs font-mono text-gray-500 truncate" title={tx.beneficiary_account}>{tx.beneficiary_account}</div>}
                  <div className="text-xs text-gray-400 mt-1">{RECEPTION_METHODS[tx.reception_method]?.label || tx.reception_method}</div>
                </div>
              </div>

              {/* Proofs */}
              <div className="grid sm:grid-cols-2 gap-3">
                {tx.proof_path && (
                  <ProofCard
                    title="Preuve de dépôt"
                    subtitle="Capture envoyée par l'expéditeur"
                    src={`${BACKEND_URL}/api/transactions/${tx.code}/proof`}
                    alt={`Preuve de dépôt — ${tx.code}`}
                    onOpen={(p) => setLightbox(p)}
                    testid="proof-deposit"
                  />
                )}
                {tx.payout_proof_path && (
                  <ProofCard
                    title="Preuve de paiement au bénéficiaire"
                    subtitle={tx.payout_proof_uploaded_at ? `Téléversée le ${formatDate(tx.payout_proof_uploaded_at)}` : "Confirmée par l'agent"}
                    src={`${BACKEND_URL}/api/transactions/${tx.code}/payout-proof`}
                    alt={`Preuve de paiement — ${tx.code}`}
                    onOpen={(p) => setLightbox(p)}
                    testid="proof-payout"
                  />
                )}
                {!tx.proof_path && !tx.payout_proof_path && (
                  <div className="sm:col-span-2 text-xs text-gray-500 text-center py-3">Aucun justificatif disponible pour cette transaction.</div>
                )}
              </div>

              {/* Timeline */}
              <div>
                <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                  <Clock size={16} className="text-[var(--sl-primary)]" />
                  Chronologie
                </h3>
                <ol className="space-y-3 border-l-2 border-[color:var(--sl-border)] pl-4 ml-2">
                  {(tx.timeline || []).map((t, i) => (
                    <li key={i} className="relative" data-testid={`timeline-${i}`}>
                      <span className={`absolute -left-[26px] top-0 w-5 h-5 rounded-full grid place-items-center ${t.status === "termine" ? "bg-emerald-500 text-white" : t.status === "annule" ? "bg-red-500 text-white" : "bg-[var(--sl-primary)] text-white"}`}>
                        {t.status === "termine" ? <CheckCircle2 size={11} /> : <Clock size={11} />}
                      </span>
                      <div className="text-sm font-medium">{STATUS_LABEL[t.status] || t.status}</div>
                      <div className="text-xs text-gray-500">{formatDate(t.at)}</div>
                      {t.agent_name && (
                        <div className="text-xs text-emerald-700 mt-0.5">Traité par {t.agent_name}</div>
                      )}
                      {t.event === "payout_proof_uploaded" && (
                        <div className="text-xs text-gray-500 mt-0.5">📎 Preuve de paiement ajoutée</div>
                      )}
                      {t.reason && <div className="text-xs text-amber-700 mt-0.5">Raison : {t.reason}</div>}
                    </li>
                  ))}
                </ol>
              </div>

              {/* Actions côté client : Reçu PDF (si terminé), Refaire, Suivi. */}
              <div className="flex flex-wrap gap-2 pt-3 border-t border-[color:var(--sl-border)]">
                {tx.status === "termine" && (
                  <button
                    type="button"
                    onClick={() => setLightbox({
                      src: `${BACKEND_URL}/api/transactions/${tx.code}/receipt`,
                      alt: "Reçu de transaction",
                      isPdf: true,
                    })}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 font-medium hover:bg-emerald-100 transition"
                    data-testid="action-receipt"
                  >
                    <Download size={12} /> Télécharger le reçu (PDF)
                  </button>
                )}
                <Link
                  to={`/envoyer?reuse=${tx.code}`}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[var(--sl-primary)]/10 text-[var(--sl-primary)] font-medium hover:bg-[var(--sl-primary)]/15 transition"
                  data-testid="action-resend"
                >
                  <Repeat size={12} /> Refaire ce transfert
                </Link>
                <Link
                  to={`/suivi?code=${tx.code}`}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[var(--sl-bg-alt)] text-gray-700 font-medium hover:bg-gray-200 transition"
                  data-testid="action-track"
                >
                  <ExternalLink size={12} /> Page de suivi
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
      {lightbox && (
        <ImageLightbox
          src={lightbox.src}
          alt={lightbox.alt}
          downloadHref={lightbox.src}
          isPdf={!!lightbox.isPdf}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

function Card({ label, value }) {
  return (
    <div className="rounded-xl border border-[color:var(--sl-border)] bg-[color:var(--sl-bg-alt)] p-3">
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className="font-heading text-sm font-bold mt-0.5 break-words">{value}</div>
    </div>
  );
}

function ProofCard({ title, subtitle, src, alt, onOpen, testid }) {
  return (
    <button
      type="button"
      onClick={() => onOpen?.({ src, alt })}
      className="text-left rounded-xl border border-[color:var(--sl-border)] p-4 flex items-start gap-3 hover:border-[var(--sl-primary)] hover:shadow-md transition group w-full"
      data-testid={testid}
    >
      <div className="w-10 h-10 rounded-full bg-[var(--sl-primary)]/10 grid place-items-center flex-shrink-0">
        <FileImage size={18} className="text-[var(--sl-primary)]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-medium text-sm text-[var(--sl-primary)] group-hover:underline">{title}</div>
        <div className="text-xs text-gray-500">{subtitle}</div>
        <div className="text-[11px] text-gray-400 mt-1">Cliquez pour agrandir</div>
      </div>
    </button>
  );
}
